import { useState } from 'react'

import { MAX_CANDIDATES, proposeBody } from '../../functions/_lib/range-body.js'

// The organiser's half: ask the question. P5-3 T6.
//
// ═══════════════════════════════════════════════════════════════════════════
// 🔴 THE CAP IS THE SAME NUMBER IN FOUR PLACES, AND THIS IMPORTS IT
// ═══════════════════════════════════════════════════════════════════════════
//
// TR8's ten lives in `trips/utils/ranges.py` (the service's refusal),
// `trips/migrations/0001_initial.sql` (a database CHECK), and
// `site/functions/_lib/range-body.js` (the Function's refusal, in words about
// dates). This is the fourth, and it does not write the number down: it imports
// `MAX_CANDIDATES` from the Function's module, which is the same file the
// Function itself uses and the same file the cross-language vectors are
// asserted against.
//
// ⚠️ IMPORTING FROM `functions/` INTO `src/` IS UNUSUAL HERE, so it is worth the
// line. It is a pure validation module — no Workers API, no environment, no
// credential — and Vite bundles it exactly as it bundles anything under `src/`.
// The alternative is a second copy of the number and of the refusal sentence,
// which is how three of the four copies would agree and one would not.
//
// The form simply does not offer an eleventh row, and says why. The service and
// the Function refuse it anyway — a form is not a guard, and a bot or a curl is
// not going through this component.
//
// 🔴 IT ALSO SAYS WHAT RE-ASKING COSTS. Changing the question clears every
// answer already in, because an answer is about the weeks that were on the
// screen when it was given. That is the one destructive thing in this slice, so
// the button that does it names the number it is about to throw away.

const BLANK = { start: '', end: '', tag: '' }

export default function ProposeRanges({ busy = false, existing, answered = 0, onPropose }) {
  const seeded =
    existing && existing.length > 0
      ? existing.map((range) => ({ start: range.start, end: range.end, tag: range.tag ?? '' }))
      : [{ ...BLANK }]
  const [rows, setRows] = useState(seeded)
  const [error, setError] = useState(null)

  const editing = existing && existing.length > 0
  const full = rows.length >= MAX_CANDIDATES

  const update = (index, field, value) => {
    setRows((current) =>
      current.map((row, at) => (at === index ? { ...row, [field]: value } : row)),
    )
  }

  const submit = async (event) => {
    event.preventDefault()
    setError(null)
    // The SAME validator the Function runs, so the sentence a member reads is
    // the sentence the Function would have sent back — one round trip earlier.
    const { payload, error: refusal } = proposeBody({
      candidate_ranges: rows
        .filter((row) => row.start !== '' || row.end !== '' || row.tag !== '')
        .map((row) => (row.tag.trim() === '' ? { start: row.start, end: row.end } : row)),
    })
    if (refusal) {
      setError(refusal)
      return
    }
    await onPropose(payload)
  }

  return (
    <form className="d2d-propose" onSubmit={submit} aria-label="Propose date ranges">
      <p className="d2d-micro d2d-propose__eyebrow">
        {editing ? 'Propose different ranges' : 'Put the weeks up'}
      </p>

      <ul className="d2d-propose__rows">
        {rows.map((row, index) => (
          // eslint-disable-next-line react/no-array-index-key
          <li key={index} className="d2d-propose__row">
            <span className="d2d-propose__field">
              <label className="d2d-micro" htmlFor={`range-start-${index}`}>
                From
              </label>
              <input
                id={`range-start-${index}`}
                type="date"
                value={row.start}
                required
                onChange={(event) => update(index, 'start', event.target.value)}
              />
            </span>
            <span className="d2d-propose__field">
              <label className="d2d-micro" htmlFor={`range-end-${index}`}>
                To
              </label>
              <input
                id={`range-end-${index}`}
                type="date"
                value={row.end}
                required
                onChange={(event) => update(index, 'end', event.target.value)}
              />
            </span>
            <span className="d2d-propose__field">
              <label className="d2d-micro" htmlFor={`range-tag-${index}`}>
                Label
              </label>
              <input
                id={`range-tag-${index}`}
                value={row.tag}
                maxLength={60}
                placeholder="school holidays"
                onChange={(event) => update(index, 'tag', event.target.value)}
              />
            </span>
            {rows.length > 1 ? (
              <button
                type="button"
                className="d2d-propose__remove"
                data-testid={`propose-remove-${index}`}
                onClick={() => setRows((current) => current.filter((_, at) => at !== index))}
              >
                Take it off
              </button>
            ) : null}
          </li>
        ))}
      </ul>

      <p className="d2d-propose__hint">
        {full
          ? `Ten is the cap — more than that and people stop answering.`
          : `${rows.length} of ${MAX_CANDIDATES}. Ten is the cap.`}
      </p>

      <div className="d2d-propose__actions">
        <button
          type="button"
          className="d2d-propose__add"
          disabled={full}
          data-testid="propose-add"
          onClick={() => setRows((current) => [...current, { ...BLANK }])}
        >
          Another week
        </button>
        <button
          type="submit"
          className="d2d-propose__submit"
          disabled={busy}
          data-testid="propose-submit"
        >
          {busy ? 'Sending…' : editing ? 'Ask again' : 'Ask everyone'}
        </button>
      </div>

      {editing && answered > 0 ? (
        <p className="d2d-propose__warning" data-testid="propose-warning">
          Changing the weeks clears the {answered}{' '}
          {answered === 1 ? 'answer' : 'answers'} already in — an answer is about the weeks that
          were on the screen when it was given.
        </p>
      ) : null}

      {error ? (
        <p className="d2d-notice d2d-notice--warn" role="status" data-testid="propose-error">
          {error}
        </p>
      ) : null}
    </form>
  )
}
